import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import AssessmentTitle from 'components/AssessmentTitle'
import AddNewIcon from 'assets/icons/add-new'
import Accordion from 'components/Accordion'
import { congregationOptions } from 'constants/congregationOptions'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import CongregationContent from './congregationContent'
import CongregationHeader from './congregationHeader'
import {
  createCongregationsItem,
  getCongregationsList,
  removeSpeciesCongregation
} from 'api/congregations'
import { showErrorToast } from 'utils/toast'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { resolveSiteWorkflowContext } from 'utils/resolveSiteWorkflowIds'

import {
  canAddMoreCongregations,
  SEASONS_PER_CONGREGATION_GROUP,
  sortCongregationsByCreatedAtAsc
} from 'utils/congregationHelpers'
import { AddNewCongregationBtnWrapper, CongregationListTitle, CongregationListTitleWrapper } from './style'
import PropTypes from 'prop-types'

export const MAX_CONGREGATIONS_PER_SITE = congregationOptions.length * SEASONS_PER_CONGREGATION_GROUP

const defaultCongregation = {
  CongregationGroupId: null,
  min: null,
  best: null,
  max: null,
  year_min: null,
  year_max: null,
  season_id: null,
  description: '',
  id: 'defaultTestId'
}

export const getAvailableCongregationsOptions = (_list, options) => {
  return options
}

const CongregationsList = ({ site, setSiteProposalFieldValidation, showCongregations = false }) => {
  const isAbleToAdd = site.SiteStatusId === SITE_STATUS_DRAFT_ID
  const params = useParams()
  const { siteId, historyId } = useMemo(() => resolveSiteWorkflowContext(site, params), [site, params])
  const [congregationList, setCongregationList] = useState([])
  const [availableCongregationsOptions, setAvailableCongregationOptions] = useState([])
  const [itemToRemove, setItemToRemove] = useState(false)
  const [activeNewTriggerId, setActiveNewTriggerId] = useState(null)

  const getSpeciesCongregationList = () => {
    try {
      getCongregationsList(siteId, historyId ?? site?.latestLiveHistoryId ?? null).then((data) => {
        if (!Array.isArray(data)) {
          return showErrorToast(data?.error || 'Failed to load congregations')
        }
        // Oldest first so the most recently added congregation appears at the bottom
        const sortedData = sortCongregationsByCreatedAtAsc(data)
        setCongregationList(sortedData)
        setAvailableCongregationOptions(getAvailableCongregationsOptions(sortedData, congregationOptions))
      })
    } catch (e) {
      showErrorToast(e)
    }

  }

  const updateCongregationList = (congregation) => {
    if (!congregation?.id) {
      return
    }
    setCongregationList((prevList) => {
      const foundIndex = prevList.findIndex((item) => item.id === congregation.id)
      if (foundIndex === -1) {
        return prevList
      }
      const listCopy = [...prevList]
      listCopy[foundIndex] = { ...congregation }
      return listCopy
    })
  }

  const createCongregations = (id) => {
    try {
      createCongregationsItem(siteId, id, historyId).then((data) => {
        if (data.error) {
          return showErrorToast(data.error)
        }
        setCongregationList((prevList) => {
          const placeholderIndex = prevList.findIndex((element) => element.id === 'defaultTestId')
          let newList
          if (placeholderIndex !== -1) {
            newList = [...prevList]
            newList[placeholderIndex] = data
          } else {
            newList = [...prevList.filter((element) => element.id !== 'defaultTestId'), data]
          }
          setAvailableCongregationOptions(getAvailableCongregationsOptions(newList, congregationOptions))
          return newList
        })
        // Keep the open accordion panel on the newly created congregation
        setActiveNewTriggerId(String(data.id))
      })
    } catch (e) {
      showErrorToast(e)
    }
  }

  const addCongregations = () => {
    setSiteProposalFieldValidation(defaultSiteProposalValidationResults)
    const isDefaultCongregationPresent = congregationList.some((element) => {
      return element.id === 'defaultTestId'
    })
    if (isDefaultCongregationPresent || !canAddMoreCongregations(congregationList, MAX_CONGREGATIONS_PER_SITE)) {
      return
    }
    setCongregationList((s) => {
      return [...s, { ...defaultCongregation }]
    })
    setActiveNewTriggerId('defaultTestId')
  }

  const removeCongregations = (item) => {
    setSiteProposalFieldValidation(defaultSiteProposalValidationResults)
    if (item.id === 'defaultTestId') {
      const newList = [...congregationList]
      const indexToRemove = congregationList.findIndex((element) => {
        return element.id === item.id
      })

      newList.splice(indexToRemove, 1)
      setAvailableCongregationOptions(getAvailableCongregationsOptions(newList, congregationOptions))
      setCongregationList(newList)
      setItemToRemove(false)
      if (String(activeNewTriggerId) === 'defaultTestId') {
        setActiveNewTriggerId(null)
      }
      return
    }

    try {
      removeSpeciesCongregation(siteId, item.id, historyId, item.historyId).then((data) => {
        if (data.error) {
          return showErrorToast(data.error)
        }
        setItemToRemove(false)
        if (String(activeNewTriggerId) === String(item.id)) {
          setActiveNewTriggerId(null)
        }
        getSpeciesCongregationList()
      })
    } catch (err) {
      showErrorToast(err)
    }
  }

  const openRemoveModal = (item) => {
    setItemToRemove(item)
  }

  const closeRemoveModal = () => {
    setItemToRemove(false)
  }


  useEffect(() => {
    if (!showCongregations) {
      return
    }
    getSpeciesCongregationList()
  }, [showCongregations])

  const oldCongregationList = congregationList.filter((element) => {
    return element.lastReassessmentDate
  })

  const newCongregationList = congregationList.filter((element) => {
    return !element.lastReassessmentDate
  })

  if (!showCongregations) {
    return null
  }

  if (site.SiteStatusId !== SITE_STATUS_DRAFT_ID && congregationList.length === 0) {
    return null
  }

  return (
    <Container>
      <AssessmentTitle
        titleId='congregationTitle'
        subTitleId='congregationSubTitle'
        fontSizePageSubtitle='16px'
        marginTop='96px'
        marginBottom='96px'
      />
      <Row>
        <Col md={8} mdOffset={2} dataTest='congregation-form'>
          {(congregationList.length === 0) ? (
              <CongregationContent
                item={defaultCongregation}
                createCongregations={createCongregations}
                options={availableCongregationsOptions}
                site={site}
                params={params}
                defaultItem={true}
                congregationList={congregationList}
              />
          ) : (
            <>
              {site.SiteStatusId === SITE_STATUS_DRAFT_ID ? (
                <>
                  {oldCongregationList.length > 0 && (
                    <>
                      <Row>
                        <Col md={8} mdOffset={2}>
                          <CongregationListTitleWrapper>
                            <CongregationListTitle>
                              <FormattedMessage
                                id='existingTriggers' />
                            </CongregationListTitle>
                          </CongregationListTitleWrapper>
                        </Col>
                      </Row>
                      <Accordion
                        items={oldCongregationList}
                        contentProps={{
                          updateCongregationList,
                          createCongregations,
                          options: availableCongregationsOptions,
                          site,
                          params,
                          defaultItem: false,
                          getSpeciesCongregationList,
                          congregationList
                        }}
                        headerProps={{ openRemoveModal, site, newTriggers: false, oldTriggers: true }}
                        contentComponent={CongregationContent}
                        headerComponent={CongregationHeader}
                        initialActive={null}
                      />
                    </>
                  )}
                  {newCongregationList.length > 0 && (
                    <>
                      <Row>
                        <Col md={8} mdOffset={2}>
                          <CongregationListTitleWrapper addMarginTop={oldCongregationList.length > 0}>
                            <CongregationListTitle>
                              <FormattedMessage
                                id='newTriggers' />
                            </CongregationListTitle>
                          </CongregationListTitleWrapper>
                        </Col>
                      </Row>
                      <Accordion
                        items={newCongregationList}
                        contentProps={{
                          updateCongregationList,
                          createCongregations,
                          options: availableCongregationsOptions,
                          site,
                          params,
                          defaultItem: false,
                          getSpeciesCongregationList,
                          congregationList
                        }}
                        headerProps={{ openRemoveModal, site, newTriggers: true, oldTriggers: false }}
                        contentComponent={CongregationContent}
                        headerComponent={CongregationHeader}
                        activeItemId={activeNewTriggerId}
                        onActiveItemIdChange={setActiveNewTriggerId}
                        initialActive={null}
                      />
                    </>
                  )}
                </>
              ) : (
                <Accordion
                  items={congregationList}
                  contentProps={{
                    updateCongregationList,
                    createCongregations,
                    options: availableCongregationsOptions,
                    site,
                    params,
                    defaultItem: false,
                    getSpeciesCongregationList,
                    congregationList
                  }}
                  headerProps={{ openRemoveModal, site, newTriggers: false, oldTriggers: false }}
                  contentComponent={CongregationContent}
                  headerComponent={CongregationHeader}
                />
              )}
            </>
          )}
        </Col>
      </Row>
      {(isAbleToAdd && canAddMoreCongregations(congregationList, MAX_CONGREGATIONS_PER_SITE) && congregationList.length !== 0) && (
        <Row>
          <Col md={8} mdOffset={2}>
            <AddNewCongregationBtnWrapper onClick={addCongregations}>
              <AddNewIcon />
              <FormattedMessage id='addNewCongregation' />
            </AddNewCongregationBtnWrapper>
          </Col>
        </Row>
      )}
      <Modal
        width='416px'
        isOpen={itemToRemove}
        toggleModal={closeRemoveModal}
        modalTitleId='removeCongregations'
        bottomBorder={true}
      >
        <FlowRequestModal
          cancelHandler={closeRemoveModal}
          item={itemToRemove}
          confirmBtnText='confirm'
          text={<FormattedMessage id='removeCongregationModalBody' values={{ name: <b>{itemToRemove.name}</b> }} />}
          confirmHandler={removeCongregations}
          rejectBtnText='cancel'
        />
      </Modal>
    </Container>
  )
}
CongregationsList.propTypes = {
  site: PropTypes.object.isRequired,
  setSiteProposalFieldValidation: PropTypes.func.isRequired,
  showCongregations: PropTypes.bool
}

export default CongregationsList
